import React, { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { updateMetaTags } from '../utils/seo'

const MacBookDisplayReparaturPage: React.FC = () => {
  const navigate = useNavigate()
  const go = (path: string) => { navigate(path); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 50) }

  useEffect(() => {
    updateMetaTags({
      title: 'MacBook Display Reparatur – Alle Modelle ab 229€ | PhoneMedic',
      description: 'MacBook Display Reparatur ✓ MacBook Pro & Air alle Modelle ✓ Retina & Liquid Retina XDR ✓ ab 229€ ✓ 12 Monate Garantie ✓ Bundesweiter Einsende-Service | PhoneMedic',
      keywords: ['MacBook Display Reparatur', 'MacBook Bildschirm kaputt', 'MacBook Pro Display tauschen', 'MacBook Air Display', 'MacBook Display Kosten', 'MacBook Retina Display'],
      canonicalUrl: 'https://www.phone-medic.de/macbook-display-reparatur',
    })

    const schema = [
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'MacBook Display Reparatur',
        description: 'Professionelle MacBook Display Reparatur für alle Modelle. Retina, Liquid Retina XDR, MacBook Pro und Air – Display tauschen mit 12 Monaten Garantie.',
        provider: { '@type': 'LocalBusiness', name: 'PhoneMedic', url: 'https://www.phone-medic.de', telephone: '+491605010907' },
        offers: [
          { '@type': 'Offer', name: 'MacBook Air 13" Display', price: '229.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'MacBook Pro 13" Display', price: '249.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'MacBook Pro 14" Display', price: '349.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
          { '@type': 'Offer', name: 'MacBook Pro 16" Display', price: '449.00', priceCurrency: 'EUR', priceValidUntil: '2026-12-31' },
        ],
        areaServed: 'DE',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'Was kostet ein MacBook Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Die Preise variieren je nach Modell: MacBook Air 13" ab 229€, MacBook Pro 13" ab 249€, MacBook Pro 14" ab 349€, MacBook Pro 16" ab 449€, MacBook Air 15" ab 299€.' },
          },
          {
            '@type': 'Question',
            name: 'Wie lange dauert ein MacBook Display Tausch?',
            acceptedAnswer: { '@type': 'Answer', text: 'Der eigentliche Display-Tausch dauert 2–3 Stunden. Inklusive Versandzeit (Hin- und Rückversand) rechne mit 4–6 Werktagen.' },
          },
          {
            '@type': 'Question',
            name: 'Gehen bei einem Display-Tausch Daten verloren?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nein. Beim Display-Tausch wird nur das Display ausgetauscht. Die SSD mit deinen Daten bleibt vollständig unberührt.' },
          },
          {
            '@type': 'Question',
            name: 'Welche Display-Typen können repariert werden?',
            acceptedAnswer: { '@type': 'Answer', text: 'Wir tauschen alle MacBook Display-Typen: Retina IPS (MacBook Air, MacBook Pro bis 2020), Liquid Retina (MacBook Air M2, M3), Liquid Retina XDR (MacBook Pro 14/16 Zoll mit ProMotion). Alle Displays in Original Apple Qualität.' },
          },
          {
            '@type': 'Question',
            name: 'Mein MacBook zeigt kein Bild – ist das ein Display-Defekt?',
            acceptedAnswer: { '@type': 'Answer', text: 'Nicht zwingend. Kein Bild kann auch ein defekter Backlight-IC auf der Platine sein (kein Display-Tausch nötig), ein Kabelbruch oder tatsächlich ein defektes Display. Wir diagnostizieren zuerst die genaue Ursache.' },
          },
        ],
      },
    ]

    let script = document.querySelector('#macbook-display-schema') as HTMLScriptElement
    if (!script) {
      script = document.createElement('script')
      script.id = 'macbook-display-schema'
      script.type = 'application/ld+json'
      document.head.appendChild(script)
    }
    script.textContent = JSON.stringify(schema)
    return () => { document.querySelector('#macbook-display-schema')?.remove() }
  }, [])

  const prices = [
    { model: 'MacBook Air 13" (M1)', price: 'ab 229 €' },
    { model: 'MacBook Air 13" (M2, M3)', price: 'ab 249 €' },
    { model: 'MacBook Air 15" (M2, M3)', price: 'ab 299 €' },
    { model: 'MacBook Pro 13" (M1, M2, Intel)', price: 'ab 249 €' },
    { model: 'MacBook Pro 14" (M1, M2, M3, M4)', price: 'ab 349 €' },
    { model: 'MacBook Pro 16" (M1, M2, M3, M4)', price: 'ab 449 €' },
    { model: 'MacBook Pro 13" (Intel, 2016–2020)', price: 'ab 229 €' },
    { model: 'MacBook Pro 15" (Intel)', price: 'ab 299 €' },
  ]

  const damages = [
    { icon: '💥', title: 'Gesprungenes Display', desc: 'Riss oder Bruch durch Sturz oder Druck. Das Display zeigt schwarze Flecken, Streifen oder ist komplett dunkel.' },
    { icon: '⬛', title: 'Schwarzer Bildschirm', desc: 'Das MacBook startet, aber der Bildschirm bleibt schwarz. Kann ein Display-Defekt oder ein Backlight-IC Problem sein.' },
    { icon: '📺', title: 'Streifen / Pixel-Fehler', desc: 'Horizontale oder vertikale Streifen, fehlerhafte Pixel oder Farbverfälschungen auf dem Display.' },
    { icon: '🔆', title: 'Hintergrundbild fehlt', desc: 'Display leuchtet nicht – Bild nur sichtbar wenn man mit einer Taschenlampe draufleuchtet. Oft Backlight-IC auf der Platine.' },
    { icon: '💧', title: 'Wasserschaden Display', desc: 'Flüssigkeit unter dem Display hinterlässt Flecken, "Wolken" oder beschädigt die Displayelektronik.' },
    { icon: '📐', title: 'Scharnierschaden', desc: 'Gebrochenes Scharnier oder geknicktes Displaykabel – oft erkennbar an wackelndem Display oder Bild das beim Aufklappen ausfällt.' },
  ]

  const faqs = [
    { q: 'Was kostet ein MacBook Display Tausch?', a: 'MacBook Air 13" ab 229€, Pro 13" ab 249€, Pro 14" ab 349€, Pro 16" ab 449€. Genaue Preise für dein Modell auf Anfrage.' },
    { q: 'Wie lange dauert ein MacBook Display Tausch?', a: 'Der eigentliche Tausch dauert 2–3 Stunden. Inklusive Versand rechne mit 4–6 Werktagen.' },
    { q: 'Gehen bei einem Display-Tausch Daten verloren?', a: 'Nein. Beim Display-Tausch wird nur das Display ausgetauscht. Deine Daten bleiben vollständig erhalten.' },
    { q: 'Welche Display-Typen werden unterstützt?', a: 'Alle MacBook Display-Typen: Retina IPS, Liquid Retina (Air M2/M3), Liquid Retina XDR (Pro 14/16"). Alle Displays in Original Apple Qualität.' },
    { q: 'Mein MacBook zeigt kein Bild – ist das ein Display-Defekt?', a: 'Nicht zwingend. Kein Bild kann auch ein defekter Backlight-IC auf der Platine sein. Wir diagnostizieren die genaue Ursache – das ist oft günstiger als ein Display-Tausch.' },
    { q: 'Kann ich mein MacBook bundesweit einsenden?', a: 'Ja. Du erhältst ein kostenloses DHL-Versandlabel. Nach der Reparatur kommt dein MacBook versichert zurück.' },
  ]

  return (
    <div className="min-h-screen bg-white">
      <section
        className="relative text-white py-20"
        style={{ backgroundImage: 'url(/hero-bg.webp)', backgroundSize: 'cover', backgroundPosition: 'center top' }}
      >
        <div className="absolute inset-0 bg-gray-900/80" />
        <div className="relative max-w-7xl mx-auto px-4">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-indigo-600/20 border border-indigo-500/30 text-indigo-300 text-sm font-medium px-3 py-1 rounded-full mb-6">
              <span className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></span>
              Retina · Liquid Retina XDR · alle Modelle
            </div>
            <h1 className="text-5xl font-bold mb-6 leading-tight">
              MacBook Display Reparatur –<br />
              <span className="text-indigo-400">Alle Modelle & Preise</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl">
              Gesprungenes Display, schwarzer Bildschirm oder Streifen? Wir tauschen MacBook Displays für alle Modelle – professionell mit 12 Monaten Garantie, bundesweiter Einsende-Service.
            </p>
            <div className="flex flex-wrap gap-4">
              <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
                Display reparieren lassen
              </a>
              <button onClick={() => go('/macbook-reparatur?tab=reparatur')} className="bg-white/10 text-white border border-white/20 px-7 py-3.5 rounded-lg font-semibold hover:bg-white/20 transition-colors">
                Modell & Preis wählen
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gray-800 text-white py-5 border-t border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { number: 'ab 229 €', label: 'Display-Tausch' },
              { number: '2–3h', label: 'Reparaturzeit' },
              { number: '12 Monate', label: 'Garantie' },
              { number: 'Bundesweit', label: 'Einsende-Service' },
            ].map((s, i) => (
              <div key={i}>
                <div className="text-2xl font-bold text-indigo-400">{s.number}</div>
                <div className="text-sm text-gray-400">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Welche Displayschäden können wir beheben?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {damages.map((d, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{d.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">Preise nach Modell</h2>
            <p className="text-gray-500">Alle Preise inklusive Einbau und 12 Monate Garantie</p>
          </div>
          <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
            <div className="grid grid-cols-2 bg-gray-800 text-white px-6 py-3 text-sm font-semibold">
              <span>Modell</span>
              <span className="text-right">Preis</span>
            </div>
            {prices.map((p, i) => (
              <div key={i} className={`grid grid-cols-2 px-6 py-4 text-sm border-b border-gray-200 last:border-0 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                <span className="text-gray-700 font-medium">{p.model}</span>
                <span className="text-right font-bold text-indigo-600">{p.price}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 mt-3 text-center">Dein Modell nicht dabei? Einfach per E-Mail anfragen – wir reparieren alle MacBook Modelle.</p>
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-10">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{faq.q}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Weitere MacBook Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'MacBook Akku tauschen', path: '/macbook-akku-tauschen' },
              { label: 'MacBook startet nicht', path: '/macbook-startet-nicht' },
              { label: 'MacBook Wasserschaden', path: '/macbook-wasserschaden' },
              { label: 'Logicboard Reparatur', path: '/macbook-logicboard-reparatur' },
            ].map((item) => (
              <button key={item.path} onClick={() => go(item.path)} className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-sm font-medium text-gray-700 hover:border-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors text-center">
                {item.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold mb-3">MacBook Display kaputt? Wir reparieren es.</h2>
          <p className="text-gray-400 mb-8">Alle Modelle · ab 229 € · 12 Monate Garantie · Bundesweiter Einsende-Service</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="mailto:service@phone-medic.de" className="bg-indigo-600 text-white px-7 py-3.5 rounded-lg font-semibold hover:bg-indigo-700 transition-colors">
              Per E-Mail anfragen
            </a>
            <button onClick={() => go('/macbook-reparatur?tab=reparatur')} className="bg-white text-gray-900 px-7 py-3.5 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Modell auswählen & buchen
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}

export default MacBookDisplayReparaturPage
